import { apiRequest } from './apiClient';

export type HouseholdViewMode = 'personal' | 'combined';

export type HouseholdMember = {
  userId: string;
  role: 'owner' | 'member';
  joinedAt: string;
  name: string;
  picture?: string;
  email?: string;
  phoneNumber?: string;
  plan: string;
};

export type PendingInvite = {
  id: string;
  channel: 'email' | 'sms' | 'link';
  to: string | null;
  expiresAt: string;
  joinUrl: string;
  deepLink?: string;
  createdAt: string;
};

/** Matches GET /household response from budget-server. */
export type HouseholdResponse = {
  id: string;
  name: string | null;
  ownerUserId: string;
  hasPro: boolean;
  myRole: 'owner' | 'member' | null;
  memberCount: number;
  maxMembers: number;
  /** Free couple size (owner + 1). Beyond this needs Pro/group. */
  freeMemberCap?: number;
  /**
   * Free: invite until 2 members.
   * Pro: invite until maxMembers (group).
   */
  canInvite?: boolean;
  members: HouseholdMember[];
  pendingInvites: PendingInvite[];
};

export async function getHousehold(): Promise<HouseholdResponse> {
  const res = await apiRequest<{ household: HouseholdResponse }>('/household');
  return res.household;
}

export async function createHouseholdInvite(params: {
  channel: 'email' | 'sms' | 'link';
  to?: string;
}): Promise<{
  invite: {
    id: string;
    channel: string;
    to: string | null;
    expiresAt: string;
    joinUrl: string;
    deepLink?: string;
  };
  delivery?: {
    channel: string;
    sent: boolean;
    skippedReason?: string;
    error?: string;
  };
}> {
  return apiRequest('/household/invites', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export type HouseholdInviteLookup = {
  valid: boolean;
  invite?: {
    channel: 'email' | 'sms' | 'link';
    expiresAt: string;
    householdName: string | null;
    invitedByName: string;
    invitedByPicture: string | null;
    joinUrl: string;
    deepLink?: string;
  };
  error?: string;
};

/** Public preview — works signed out (optional auth on server). */
export async function lookupHouseholdInvite(token: string): Promise<HouseholdInviteLookup> {
  const q = encodeURIComponent(token.trim());
  return apiRequest(`/household/invites/lookup?token=${q}`, {
    skipAuthRefresh: true,
  });
}

export type IncomingHouseholdInvite = NonNullable<HouseholdInviteLookup['invite']> & {
  token: string;
};

/**
 * Pending invite addressed to the signed-in user's email or phone.
 * Used when they install / sign in without opening the invite link.
 */
export async function getIncomingHouseholdInvite(): Promise<{
  invite: IncomingHouseholdInvite | null;
}> {
  return apiRequest('/household/invites/incoming');
}

export async function acceptHouseholdInvite(token: string): Promise<{
  household: HouseholdResponse;
}> {
  return apiRequest('/household/invites/accept', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function revokeHouseholdInvite(id: string): Promise<{ ok: boolean }> {
  return apiRequest(`/household/invites/${id}`, { method: 'DELETE' });
}

export async function removeHouseholdMember(userId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/household/members/${userId}`, { method: 'DELETE' });
}

export async function leaveHousehold(): Promise<{ ok: boolean }> {
  return apiRequest('/household/leave', { method: 'POST' });
}
